import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Users, ChevronRight, User, Mail, Phone, Building, Link2, Briefcase, Send, Loader2, Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const Team = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    portfolio: '',
    specialization: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Auto-open form if ?apply=true is in URL
  useEffect(() => {
    if (searchParams.get('apply') === 'true') {
      setShowJoinForm(true);
    }
  }, [searchParams]);

  const teamMembers = [
    {
      name: 'David Ferfers',
      role: 'Gründer & Geschäftsführer',
      description: 'Visionär und Stratege hinter RevoCut. Verantwortlich für Partnerschaften und Geschäftsentwicklung.',
      initials: 'DF',
      image: '/david-ferfers.png',
    },
    {
      name: 'Lennart Altheim',
      role: 'Gründer & Geschäftsführer',
      description: 'Verantwortet Wachstum und operative Umsetzung bei RevoCut. Fokus auf Marketing/Content, Kundenführung und Cutter-Management.',
      initials: 'LA',
      image: '/lennart-altheim.png',
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      console.log('Sending application:', formData);
      console.log('API URL:', API_URL);
      
      const response = await fetch(`${API_URL}/api/freelancer-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          website: '',
          portfolio: '',
          specialization: '',
          message: ''
        });
      } else {
        const data = await response.json();
        // Handle Pydantic validation errors (array format)
        if (Array.isArray(data.detail)) {
          const errorMessages = data.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
          setError(errorMessages || 'Validierungsfehler');
        } else {
          setError(data.detail || 'Fehler beim Senden der Bewerbung');
        }
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuche es später erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Das Team hinter{' '}
            <span style={{ color: '#5C63FF' }}>RevoCut</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed">
            Wir sind ein junges Team mit einer großen Vision: Die Video-Produktion für jeden zugänglich machen.
          </p>
        </div>
      </section>

      {/* Team Members - David Media Style */}
      <section className="px-6 lg:px-12 py-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-12" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Unser <span style={{ color: '#5C63FF' }}>Team</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
          {teamMembers.map((member, index) => (
            <div 
              key={index}
              className="relative overflow-hidden rounded-2xl border border-white/10 hover:border-white/20 transition-all group"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
            >
              {/* Profile Image */}
              <div 
                className="h-64 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(92, 99, 255, 0.1)' }}
              >
                {member.image ? (
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white/20">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div 
                    className="w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold"
                    style={{ backgroundColor: '#5C63FF', fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {member.initials}
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {member.name}
                </h3>
                <p className="text-sm mb-4" style={{ color: '#5C63FF' }}>
                  {member.role}
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {member.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Join Team CTA */}
      <section className="px-6 lg:px-12 py-16">
        <div 
          className="p-8 md:p-12 rounded-2xl"
          style={{ backgroundColor: 'rgba(92, 99, 255, 0.1)', border: '1px solid rgba(92, 99, 255, 0.3)' }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Du bist Freelancer?
              </h2>
              <p className="text-gray-400">
                Werde Teil unseres Netzwerks und erhalte Zugang zu spannenden Projekten.
              </p>
            </div>
            <button 
              onClick={() => setShowJoinForm(true)}
              className="px-8 py-4 rounded-full text-base font-semibold transition-all hover:scale-105 flex items-center gap-2 whitespace-nowrap"
              style={{ backgroundColor: '#5C63FF' }}
            >
              Team beitreten
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="px-6 lg:px-12 py-16">
        <div 
          className="p-8 md:p-12 rounded-2xl border border-white/10"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Unsere <span style={{ color: '#5C63FF' }}>Vision</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-gray-400 leading-relaxed mb-4">
                RevoCut wurde mit einer einfachen Idee gegründet: Es sollte einfacher sein, 
                professionelle Post-Production zu buchen. Keine versteckten Kosten, keine 
                komplizierten Prozesse.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Wir verbinden Unternehmen mit einem wachsenden Netzwerk von geprüften 
                Freelancern für Video-Editing, Motion Design und mehr.
              </p>
            </div>
            <div>
              <p className="text-gray-400 leading-relaxed mb-4">
                Unsere Plattform ermöglicht es, Projekte transparent zu kalkulieren und den 
                perfekten Freelancer für jedes Budget zu finden.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Wir glauben an Qualität, Transparenz und faire Preise – für Kunden und 
                Kreative gleichermaßen.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Freelancer Application Modal */}
      {showJoinForm && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowJoinForm(false); setSubmitted(false); setError(''); } }}
          style={{ paddingTop: '80px', paddingBottom: '20px' }}
        >
          <div 
            className="rounded-2xl border border-white/10 w-full max-w-2xl overflow-y-auto mx-4" 
            style={{ backgroundColor: '#0a0a0a', maxHeight: 'calc(100vh - 120px)' }}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-white/10 sticky top-0 z-10" style={{ backgroundColor: '#0a0a0a' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}>
                  <Briefcase className="w-5 h-5 text-purple-400" />
                </div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Als Freelancer bewerben
                </h2>
              </div>
              <button 
                onClick={() => { setShowJoinForm(false); setSubmitted(false); setError(''); }}
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors text-xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {submitted ? (
                <div className="text-center py-12">
                  <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}
                  >
                    <Check className="w-10 h-10 text-green-400" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Bewerbung gesendet!
                  </h3>
                  <p className="text-gray-400 mb-6">
                    Vielen Dank! Wir melden uns innerhalb von 2-3 Werktagen bei dir.
                  </p>
                  <button 
                    onClick={() => { setShowJoinForm(false); setSubmitted(false); }}
                    className="px-8 py-3 rounded-xl font-medium transition-all bg-purple-500 hover:bg-purple-600"
                  >
                    Schließen
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
                      {error}
                    </div>
                  )}

                  {/* Persönliche Daten */}
                  <div className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-purple-400" />
                      Persönliche Daten
                    </h3>

                    <div className="space-y-4">
                      {/* Name */}
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Name *</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="Max Mustermann"
                            className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">E-Mail *</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="max@beispiel.de"
                            className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Telefon</label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+49 123 456789"
                            className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>

                      {/* Company */}
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Firma (optional)</label>
                        <div className="relative">
                          <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            placeholder="Meine Agentur GmbH"
                            className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Berufliche Erfahrung */}
                  <div className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-purple-400" />
                      Berufliche Erfahrung
                    </h3>

                    <div className="space-y-4">
                      {/* Specialization */}
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Spezialisierung *</label>
                        <select
                          name="specialization"
                          value={formData.specialization}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-purple-500"
                          style={{ backgroundColor: '#1a1a1a' }}
                        >
                          <option value="">Bitte wählen...</option>
                          <option value="video_editing">Video-Editing / Schnitt</option>
                          <option value="motion_design">Motion Design / Animation</option>
                          <option value="color_grading">Color Grading</option>
                          <option value="sound_design">Sound Design / Audio</option>
                          <option value="vfx">Visual Effects (VFX)</option>
                          <option value="full_production">Full-Service Produktion</option>
                          <option value="other">Sonstiges</option>
                        </select>
                      </div>

                      {/* Website */}
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Website (optional)</label>
                        <div className="relative">
                          <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="url"
                            name="website"
                            value={formData.website}
                            onChange={handleInputChange}
                            placeholder="https://meineseite.de"
                            className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>

                      {/* Portfolio */}
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Portfolio / Showreel URL (optional)</label>
                        <div className="relative">
                          <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="url"
                            name="portfolio"
                            value={formData.portfolio}
                            onChange={handleInputChange}
                            placeholder="https://vimeo.com/mein-showreel"
                            className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Nachricht (optional)</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          rows={3}
                          placeholder="Erzähl uns etwas über dich und deine Erfahrung..."
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 rounded-xl font-bold text-lg transition-all bg-purple-500 hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Bewerbung absenden
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    Mit dem Absenden stimmst du unserer{' '}
                    <button type="button" onClick={() => navigate('/datenschutz')} className="underline hover:text-white">
                      Datenschutzerklärung
                    </button>{' '}
                    zu.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
